import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

import { TIME_PUBLISHED_FORMAT } from '@/core/constants/time';

import {
  useAiAnalysisStream,
  type AiAnalysisFileId,
  type AiAnalysisTokensUsed,
} from './aiAnalysis/useAiAnalysisStream';
import type { ChatMessage } from '../components/ChatContent';
import { filesToPayloads } from '../utils/aiAnalysisFiles';

const formatTimestamp = (date: Date) => dayjs(date).format(TIME_PUBLISHED_FORMAT);

const DISTANCE_FROM_BOTTOM_THRESHOLD = 24;

type UseChatBotOptions = {
  focusId?: number | null;
};

const DISPLAY_ERROR_MESSAGE = 'Unexpected error, try again later.';

const getErrorMessage = (error: string): string => {
  if (/quota|limit.*token|exceeded/i.test(error)) {
    return 'Request limit exceeded. Please try again later.';
  }

  return error;
};

const isTokensUsed = (value: unknown): value is AiAnalysisTokensUsed => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const tokens = value as { [key: string]: unknown };

  return (
    typeof tokens.total_tokens === 'number' &&
    typeof tokens.input_tokens === 'number' &&
    typeof tokens.output_tokens === 'number' &&
    typeof tokens.file_tokens === 'number'
  );
};

const parseFileIds = (value: unknown): Array<AiAnalysisFileId> | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const fileIds = value.filter(
    (item): item is AiAnalysisFileId =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof (item as AiAnalysisFileId).id === 'string' &&
      typeof (item as AiAnalysisFileId).file_type === 'string'
  );

  return fileIds;
};

export const useChatBot = ({ focusId }: UseChatBotOptions) => {
  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const streamMessageIdRef = useRef<string | null>(null);
  const hasStreamedDataRef = useRef(false);
  const hasReplacedWithFullRef = useRef(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const analysisThreadIdRef = useRef<string | null>(null);
  const tokensUsedRef = useRef<AiAnalysisTokensUsed | null>(null);
  const fileIdsRef = useRef<Array<AiAnalysisFileId>>([]);
  const streamedTextRef = useRef('');
  const [canAttachFiles, setCanAttachFiles] = useState(true);
  const { start, update, stop, error } = useAiAnalysisStream();

  const appendMessageText = useCallback((messageId: string, chunk: string) => {
    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              text: `${message.text ?? ''}${chunk}`,
            }
          : message
      )
    );
  }, []);

  const setMessageText = useCallback((messageId: string, text: string) => {
    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              text,
            }
          : message
      )
    );
  }, []);

  useEffect(() => {
    if (!contentRef.current || !autoScrollEnabled) {
      return;
    }

    contentRef.current.scrollTop = contentRef.current.scrollHeight;
  }, [messages, autoScrollEnabled]);

  const handleChatScroll = useCallback(() => {
    const box = contentRef.current;

    if (!box) {
      return;
    }

    const distanceFromBottom = box.scrollHeight - box.scrollTop - box.clientHeight;
    const nearBottom = distanceFromBottom < DISTANCE_FROM_BOTTOM_THRESHOLD;

    setAutoScrollEnabled(nearBottom);
  }, []);

  const scrollToBottom = useCallback(() => {
    const box = contentRef.current;

    if (!box) {
      return;
    }

    box.scrollTop = box.scrollHeight;
    setAutoScrollEnabled(true);
  }, []);

  const finalizeStreamMessage = useCallback(() => {
    if (!streamMessageIdRef.current) {
      return;
    }

    const messageId = streamMessageIdRef.current;

    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              isGenerating: false,
            }
          : message
      )
    );
  }, []);

  const stopStream = useCallback(() => {
    stop();
    finalizeStreamMessage();
    streamMessageIdRef.current = null;
    hasStreamedDataRef.current = false;
    hasReplacedWithFullRef.current = false;
    streamedTextRef.current = '';
    setIsStreaming(false);
  }, [finalizeStreamMessage, stop]);

  const handleChunk = useCallback(
    (payload: string) => {
      if (!streamMessageIdRef.current) {
        return;
      }

      let textChunk = payload;

      let replaceWithFullContent = false;

      try {
        const parsed = JSON.parse(payload) as {
          text?: string;
          content?: string;
          result?: string;
          updated_analysis?: string;
          type?: string;
          analysis_thread_id?: string;
          tokens_used?: unknown;
          file_ids?: unknown;
        };

        if (typeof parsed.analysis_thread_id === 'string') {
          analysisThreadIdRef.current = parsed.analysis_thread_id;
        }

        if (isTokensUsed(parsed.tokens_used)) {
          tokensUsedRef.current = parsed.tokens_used;
        }

        const fileIds = parseFileIds(parsed.file_ids);

        if (fileIds) {
          fileIdsRef.current = fileIds;
        }

        if (typeof parsed.updated_analysis === 'string') {
          textChunk = parsed.updated_analysis;
          replaceWithFullContent = true;
        } else if (parsed.type === 'completed' && typeof parsed.text === 'string') {
          textChunk = parsed.text;
          replaceWithFullContent = true;
        } else {
          const payloadText = parsed.text ?? parsed.content ?? parsed.result;

          if (typeof payloadText === 'string') {
            textChunk = payloadText;
          } else if (
            !parsed.text &&
            !parsed.content &&
            !parsed.result &&
            !parsed.updated_analysis
          ) {
            return;
          }
        }
      } catch {}

      if (!textChunk) {
        return;
      }

      if (!hasStreamedDataRef.current) {
        hasStreamedDataRef.current = true;
        setMessages(prev =>
          prev.map(message =>
            message.id === streamMessageIdRef.current
              ? {
                  ...message,
                  isGenerating: false,
                }
              : message
          )
        );
      }

      if (replaceWithFullContent) {
        hasReplacedWithFullRef.current = true;
        streamedTextRef.current = textChunk;
        setMessageText(streamMessageIdRef.current, textChunk);
      } else if (!hasReplacedWithFullRef.current) {
        const accumulated = streamedTextRef.current;

        if (
          accumulated.length > 0 &&
          textChunk.length >= accumulated.length &&
          (textChunk === accumulated || textChunk.startsWith(accumulated))
        ) {
          hasReplacedWithFullRef.current = true;
          streamedTextRef.current = textChunk;
          setMessageText(streamMessageIdRef.current, textChunk);
        } else {
          streamedTextRef.current += textChunk;
          appendMessageText(streamMessageIdRef.current, textChunk);
        }
      }
    },
    [appendMessageText, setMessageText]
  );

  const startStream = useCallback(
    async (initialRequest: string, files: Array<File> = []) => {
      stopStream();
      setStreamError(null);
      analysisThreadIdRef.current = null;
      tokensUsedRef.current = null;
      fileIdsRef.current = [];
      setIsStreaming(true);

      const messageId = `bot-${Date.now()}`;

      streamMessageIdRef.current = messageId;
      hasStreamedDataRef.current = false;
      hasReplacedWithFullRef.current = false;
      streamedTextRef.current = '';

      setMessages(prev => [
        ...prev,
        {
          id: messageId,
          role: 'bot',
          text: '',
          isGenerating: true,
          createdAt: formatTimestamp(new Date()),
        },
      ]);

      if (!focusId) {
        setStreamError('Missing focus_id.');
        setIsStreaming(false);

        return;
      }

      setCanAttachFiles(false);

      let filePayloads;

      try {
        filePayloads = files.length ? await filesToPayloads(files) : undefined;
      } catch {
        setStreamError(DISPLAY_ERROR_MESSAGE);
        toast.error('Failed to process attached files.');
        setIsStreaming(false);
        setCanAttachFiles(true);
        setMessages(prev =>
          prev.map(message =>
            message.id === messageId
              ? {
                  ...message,
                  isGenerating: false,
                  error: DISPLAY_ERROR_MESSAGE,
                }
              : message
          )
        );

        return;
      }

      void start({
        focusId,
        initialRequest: initialRequest || undefined,
        files: filePayloads,
        onChunk: handleChunk,
        onComplete: () => {
          finalizeStreamMessage();
          setIsStreaming(false);
        },
      });
    },
    [finalizeStreamMessage, focusId, handleChunk, start, stopStream]
  );

  const startUpdateStream = useCallback(
    (userMessage: string) => {
      stopStream();
      setStreamError(null);
      setIsStreaming(true);

      const messageId = `bot-${Date.now()}`;

      streamMessageIdRef.current = messageId;
      hasStreamedDataRef.current = false;
      hasReplacedWithFullRef.current = false;
      streamedTextRef.current = '';

      const threadId = analysisThreadIdRef.current;
      const tokensUsed = tokensUsedRef.current;
      const lastBotMessage = messages.filter(m => m.role === 'bot').pop();
      const existingAnalysis = lastBotMessage?.text ?? '';

      if (!focusId || !threadId) {
        setStreamError('Missing focus_id or analysis_thread_id.');
        setIsStreaming(false);

        return;
      }

      if (!tokensUsed) {
        setStreamError(DISPLAY_ERROR_MESSAGE);
        toast.error(DISPLAY_ERROR_MESSAGE);
        setIsStreaming(false);

        return;
      }

      setMessages(prev => [
        ...prev,
        {
          id: messageId,
          role: 'bot',
          text: '',
          isGenerating: true,
          createdAt: formatTimestamp(new Date()),
        },
      ]);

      void update({
        focusId,
        analysisThreadId: threadId,
        existingAnalysis,
        modificationRequest: userMessage,
        tokensUsed,
        fileIds: fileIdsRef.current.length ? fileIdsRef.current : undefined,
        onChunk: handleChunk,
        onComplete: () => {
          finalizeStreamMessage();
          setIsStreaming(false);
        },
      });
    },
    [finalizeStreamMessage, focusId, handleChunk, messages, stopStream, update]
  );

  const handleSend = useCallback(
    (text: string, files: Array<File> = []) => {
      const trimmedText = text.trim();
      const fileNames = files.map(file => file.name);
      const displayText =
        trimmedText || (fileNames.length ? `Attached: ${fileNames.join(', ')}` : '');

      if (analysisThreadIdRef.current) {
        if (!trimmedText) {
          return;
        }

        setMessages(prev => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            text: displayText,
            createdAt: formatTimestamp(new Date()),
          },
        ]);
        startUpdateStream(trimmedText);

        return;
      }

      if (displayText) {
        setMessages(prev => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            text: displayText,
            createdAt: formatTimestamp(new Date()),
          },
        ]);
      }

      void startStream(trimmedText, files);
    },
    [startStream, startUpdateStream]
  );

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const messageId = streamMessageIdRef.current;
    const messageForUser = DISPLAY_ERROR_MESSAGE;

    toast.error(getErrorMessage(error));
    setStreamError(messageForUser);
    setIsStreaming(false);

    if (!analysisThreadIdRef.current) {
      setCanAttachFiles(true);
    }

    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              isGenerating: false,
              error: messageForUser,
            }
          : message
      )
    );
  }, [error]);

  const hasMessages = useMemo(() => messages.length > 0, [messages.length]);

  return {
    messages,
    isStreaming,
    streamError,
    contentRef,
    autoScrollEnabled,
    hasMessages,
    canAttachFiles,
    handleChatScroll,
    scrollToBottom,
    handleSend,
    stopStream,
  };
};
